'use client';

import React from 'react';

export interface AvatarProps {
  /** Photo URL. Falls back to initials from `name`. */
  src?: string | null;
  /** Full name — used for initials and aria. */
  name?: string;
  /** Pixel diameter. Default 40. */
  size?: number;
  /** Staff color ring: index 1–8 into the calendar palette, a CSS color, or null. */
  ring?: number | string | null;
  style?: React.CSSProperties;
}

const STAFF_COLORS = [
  'var(--staff-1)',
  'var(--staff-2)',
  'var(--staff-3)',
  'var(--staff-4)',
  'var(--staff-5)',
  'var(--staff-6)',
  'var(--staff-7)',
  'var(--staff-8)',
];

/**
 * Avatar — photo or initials on accent wash. Optional staff color ring
 * (calendar people). Initials are derived from `name` when no `src`.
 */
export function Avatar({
  src = null,
  name = '',
  size = 40,
  ring = null,
  style = {},
  ...rest
}: AvatarProps & Omit<React.HTMLAttributes<HTMLDivElement>, keyof AvatarProps>) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('');

  const ringColor =
    ring == null ? null : typeof ring === 'number' ? STAFF_COLORS[(ring - 1) % 8] : ring;

  const inner = (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--r-full)',
        background: src ? 'var(--bg-sunken)' : 'var(--brand-wash)',
        color: 'var(--accent-deep)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-sans)',
        fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
        fontSize: Math.round(size * 0.38),
        overflow: 'hidden',
        flex: '0 0 auto',
        backgroundImage: src ? `url(${src})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {!src && <bdi>{initials}</bdi>}
    </div>
  );

  if (!ringColor) {
    return (
      <div style={{ ...style }} {...rest}>
        {inner}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 2,
        borderRadius: 'var(--r-full)',
        background: ringColor,
        display: 'inline-flex',
        ...style,
      }}
      {...rest}
    >
      <div style={{ padding: 2, borderRadius: 'var(--r-full)', background: 'var(--bg-surface)' }}>
        {inner}
      </div>
    </div>
  );
}
